import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { flow, isEmpty } from 'lodash-es';
/* Models */
import { PageModel } from 'client/data/models/page';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { buildLeadTargetsPath, LeadTargetsModel } from 'client/data/models/lead-targets';
/* Constants */
import { LEAD_TYPES, LEAD_FORM_VIEWS } from 'site-modules/shared/constants/lead-form/lead-form-params';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { LEAD_FORM_CONFIGS, STRATEGIES } from 'site-modules/shared/constants/lead-form/lead-form-configs';
/* Components */
import { UnifiedLeadForm } from 'site-modules/shared/components/lead-form/unified-lead-form/unified-lead-form';
import { UserInfoPriceChecker } from 'client/site-modules/shared/components/lead-form/unified-lead-form/components/user-info/digital-retail/user-info-price-checker';
/* Utils */
import {
  enrichLeadsWithAdditionalData,
  getLeadSubmissionInfoWithoutEmail,
  getPathname,
} from 'site-modules/shared/components/lead-form/utils/utils';
import { getApiHeaders } from 'site-modules/shared/components/lead-form/unified-lead-form-pages/digital-retail-lead-form-utils';
import { getLeadFormCreativeId } from 'site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import {
  sendMarketoThankYouEmail,
  sendUsedInventoryMarketoThankYouEmail,
} from 'site-modules/shared/components/lead-form/lead-form-wrapper/lead-form-wrapper-methods';
import { UserCongratsInfoPriceChecker } from 'site-modules/shared/components/lead-form/unified-lead-form/components/user-info/digital-retail/user-congrats-info-price-checker';
import { getCarZingTags } from 'site-modules/shared/components/inventory/utils/carzing';

export function DigitalRetailPriceCheckerLeadFormUI({
  params,
  options,
  visitorId = '',
  pageName = '',
  selectedVehicle = null,
  leadTargets = [],
  useGraphQlForLoanOffers = false,
  isMobile = false,
}) {
  if (!selectedVehicle || isEmpty(leadTargets)) return null;

  const { vin, make, model, year, type, submodel, submodelIdentifier, zipCode, radius, styleId, partnerCode, isLease } =
    params;
  const {
    showLeasePayments,
    creativeId,
    isUsed,
    tmvAPIoptions,
    headerText,
    subHeaderText,
    submitButtonText,
    hidePricingTooltip,
  } = options;

  const leadAdditionalData = isLease ? { interest: 'lease' } : {};

  const customConfig = {
    leadType: LEAD_TYPES.INVENTORY,
    leadFormComponent: LEAD_FORM_VIEWS.DIGITAL_RETAIL_OFFER_TYPE,
    creativeId:
      creativeId ||
      getLeadFormCreativeId({
        isNewVin: !isUsed,
        pageNameLegacy: pageName,
        formName: LEAD_FORMS_CREATIVE_ID.DEAL_CHECK_PRICE_SINGLE_PAGE,
      }),
    processDataMethods: {
      getLeadSubmissionAPIHeaders: getApiHeaders(visitorId),
      getLeadSubmissionInfo: flow(getLeadSubmissionInfoWithoutEmail, enrichLeadsWithAdditionalData(leadAdditionalData)),
      postProcessMethods: [isUsed ? sendUsedInventoryMarketoThankYouEmail : sendMarketoThankYouEmail],
    },
    leadFormConfig: {
      submitButtonText: submitButtonText || "Get Dealer's Price",
      submitButtonColor: 'blue-50',
      headerText,
      subHeaderText,
      hidePricingTooltip,
      showLeasePayments,
      userInfoComponent: UserInfoPriceChecker,
      useGraphQlForLoanOffers,
      tmvAPIoptions,
      type,
    },
    thankYouComponent: UserCongratsInfoPriceChecker,
    thankYouConfig: {
      isMobile,
      selectedVehicle,
    },
    hideDelighted: false,
    useLeadTargetsFromProps: true,
    renderFormServerSide: true,
    tags: getCarZingTags(partnerCode),
  };
  const formProps = {
    vin,
    make,
    model,
    year,
    submodel,
    submodelIdentifier,
    styleId,
    zipCode,
    radius,
    customConfig,
    config: {
      ...LEAD_FORM_CONFIGS.UNIFIED,
      dataTrackingParent: { creativeId: customConfig.creativeId },
    },
    pathname: getPathname(options),
  };

  return <UnifiedLeadForm {...formProps} customConfig={customConfig} leadTargets={leadTargets} />;
}

DigitalRetailPriceCheckerLeadFormUI.propTypes = {
  params: PropTypes.shape({}).isRequired,
  options: PropTypes.shape({ subHeaderText: PropTypes.string, hidePricingTooltip: PropTypes.bool }).isRequired,
  selectedVehicle: InventoryEntities.InventoryVin,
  visitorId: PropTypes.string,
  pageName: PropTypes.string,
  leadTargets: PropTypes.arrayOf(PropTypes.shape({})),
  useGraphQlForLoanOffers: PropTypes.bool,
  isMobile: PropTypes.bool,
  location: PropTypes.shape({}),
};

export const mapStateToProps = state => ({
  visitorId: state.visitor.id,
  isMobile: state.mobile,
});

export const stateToPropsConfig = {
  pageName: bindToPath('legacy.pageName', PageModel),
  selectedVehicle: bindToPath(({ params: { vin } }) => (vin ? `vin["${vin}"]` : ''), InventoryModel),
  leadTargets: bindToPath(
    ({ params: { radius, vin } }) =>
      buildLeadTargetsPath({
        omitYear: true,
        omitStyleId: true,
        strategy: STRATEGIES.SINGLE,
        radius,
        vin,
      }),
    LeadTargetsModel
  ),
};

export const DigitalRetailPriceCheckerLeadForm = withRouter(
  connect(mapStateToProps)(connectToModel(DigitalRetailPriceCheckerLeadFormUI, stateToPropsConfig))
);
